import React from 'react';
import Container from '../../components/layout/Container/Container';
import { Row, Col } from '../../components/layout/Grid/Grid';
import Card, { CardBody } from '../../components/ui/Card/Card';
import cvData from '../../assets/data/cv-data';
import './About.css';

const About = () => {
  const { personalInfo, experience, education, certifications, languages } = cvData;

  return (
    <div className="about-page bg-light">
      <section className="bg-primary text-white py-5 mb-5">
        <Container>
          <Row>
            <Col className="text-center">
              <h1 className="display-4 fw-bold mb-3">About Me</h1>
              <p className="lead mb-0">Software development, teaching, and practical problem solving.</p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container>
        <Row className="mb-5">
          <Col lg={4} className="mb-4 mb-lg-0">
            <Card className="border-0 shadow text-center h-100">
              <CardBody>
                <div className="position-relative d-inline-block mb-4">
                  <img
                    src={personalInfo.photo}
                    alt={personalInfo.name}
                    className="rounded-circle img-thumbnail border-4 border-primary"
                    style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                  />
                  <span className="position-absolute bottom-0 end-0 bg-success rounded-circle p-2 border border-2 border-white"></span>
                </div>
                <h2 className="h4 fw-bold mb-1">{personalInfo.name}</h2>
                <p className="text-primary mb-3">{personalInfo.title}</p>
                <p className="text-muted small mb-3">{personalInfo.location}</p>

                <div className="d-flex justify-content-center gap-3 mb-4">
                  <a href={`mailto:${personalInfo.email}`} className="text-dark">
                    <i className="fas fa-envelope fa-lg"></i>
                  </a>
                  {personalInfo.socialLinks.github && (
                    <a href={personalInfo.socialLinks.github} target="_blank" rel="noopener noreferrer" className="text-dark">
                      <i className="fab fa-github fa-lg"></i>
                    </a>
                  )}
                  {personalInfo.socialLinks.linkedin && (
                    <a href={personalInfo.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-dark">
                      <i className="fab fa-linkedin fa-lg"></i>
                    </a>
                  )}
                </div>

                <div className="text-start">
                  <h3 className="h6 fw-bold mb-3">Contact Info</h3>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="fas fa-envelope text-primary me-2"></i>
                      {personalInfo.email}
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-phone text-primary me-2"></i>
                      {personalInfo.phone}
                    </li>
                    <li className="mb-2">
                      <i className="fas fa-map-marker-alt text-primary me-2"></i>
                      {personalInfo.location}
                    </li>
                  </ul>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="border-0 shadow h-100">
              <CardBody>
                <h2 className="h4 fw-bold mb-4">Professional Overview</h2>
                <p className="lead mb-4">{personalInfo.bio}</p>

                <h3 className="h5 fw-bold mt-4 mb-3">Languages</h3>
                <Row className="g-3 mb-4">
                  {languages.map((lang) => (
                    <Col md={4} key={lang.name}>
                      <div className="bg-light rounded p-3">
                        <h4 className="h6 fw-bold mb-2">{lang.name}</h4>
                        <p className="small text-muted mb-2">{lang.level}</p>
                        <div className="progress" style={{ height: '5px' }}>
                          <div
                            className="progress-bar bg-primary"
                            style={{ width: `${lang.proficiency}%` }}
                          ></div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <h3 className="h5 fw-bold mb-3">What I Do</h3>
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-code text-primary fa-2x me-3"></i>
                      <div>
                        <h4 className="h6 fw-bold mb-1">Web Development</h4>
                        <p className="small text-muted">Building responsive websites and full-stack applications with React, Next.js, Node.js, PHP, Angular, and Bootstrap.</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-chalkboard-teacher text-primary fa-2x me-3"></i>
                      <div>
                        <h4 className="h6 fw-bold mb-1">Technical Instruction</h4>
                        <p className="small text-muted">Teaching programming, web development, office tools, and computer science concepts through hands-on lessons.</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-database text-primary fa-2x me-3"></i>
                      <div>
                        <h4 className="h6 fw-bold mb-1">Data and Database Work</h4>
                        <p className="small text-muted">Working with MySQL, MongoDB, data analysis, machine learning concepts, and database-backed applications.</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-cloud text-primary fa-2x me-3"></i>
                      <div>
                        <h4 className="h6 fw-bold mb-1">Cloud and Delivery</h4>
                        <p className="small text-muted">Using Firebase, Docker, AWS, Azure, Git, and Agile workflows to build, test, and deliver practical software.</p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <section className="mb-5">
          <h2 className="h3 fw-bold text-center mb-5">
            <span className="border-bottom border-primary border-3 pb-2">Work Experience</span>
          </h2>

          <Row>
            {experience.map((exp) => (
              <Col md={6} lg={4} key={exp.id} className="mb-4">
                <Card className="border-0 shadow h-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                        <i className="fas fa-briefcase text-primary fa-2x"></i>
                      </div>
                      <div>
                        <h3 className="h5 fw-bold mb-1">{exp.position}</h3>
                        <p className="text-primary mb-0">{exp.company}</p>
                      </div>
                    </div>
                    <p className="text-muted small mb-3">
                      <i className="far fa-calendar-alt me-2"></i>
                      {exp.startDate} - {exp.endDate}
                    </p>
                    <p className="small mb-3">{exp.description}</p>
                    <ul className="small list-unstyled">
                      {exp.achievements.map((achievement, index) => (
                        <li key={index} className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        <Row className="mb-5">
          <Col md={6} className="mb-4 mb-md-0">
            <Card className="border-0 shadow h-100">
              <CardBody>
                <h3 className="h5 fw-bold mb-4">Education</h3>
                {education.map((edu) => (
                  <div key={edu.id} className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-primary bg-opacity-10 rounded p-2 me-3">
                        <i className="fas fa-graduation-cap text-primary"></i>
                      </div>
                      <div>
                        <h4 className="h6 fw-bold mb-1">{edu.degree}</h4>
                        <p className="small text-muted mb-0">{edu.institution}</p>
                      </div>
                    </div>
                    <p className="small text-muted mb-2">
                      {edu.startDate} - {edu.endDate}
                    </p>
                    <ul className="small list-unstyled mb-0">
                      {edu.achievements.map((achievement, index) => (
                        <li key={index} className="mb-1">
                          <i className="fas fa-star text-warning me-2"></i>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="border-0 shadow h-100">
              <CardBody>
                <h3 className="h5 fw-bold mb-4">Training and Certifications</h3>
                <div className="list-group list-group-flush">
                  {certifications.map((cert) => (
                    <a
                      key={cert.id}
                      href={cert.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="list-group-item list-group-item-action border-0 px-0"
                    >
                      <div className="d-flex align-items-center">
                        <i className="fas fa-certificate text-primary fa-2x me-3"></i>
                        <div>
                          <h4 className="h6 fw-bold mb-1">{cert.name}</h4>
                          <p className="small text-muted mb-0">
                            {cert.issuer} | {cert.date}
                          </p>
                        </div>
                        <i className="fas fa-external-link-alt ms-auto text-muted"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default About;
